// Earning Apps Review — factual dataset. Each entry carries fully
// unique fields so the generator can render <25% overlapping pages
// without templated filler.

export type Verdict = "Real" | "Mixed" | "Risky" | "Likely Fake";

export type EarningApp = {
  slug: string;
  name: string;
  category: string;               // survey / cashback / passive / play-to-earn / microtask / crypto-tap / walk-earn / data
  verdict: Verdict;
  founded: number;
  hq: string;
  regions: string[];              // primary payout regions
  minPayout: string;              // human string (e.g. "$5 via PayPal")
  payoutMethods: string[];
  earningsPerHour: string;        // conservative realistic estimate
  mechanism: string;              // 1–2 sentence unique explainer
  realSignals: string[];          // 3–5 unique bullet points
  redFlags: string[];             // 2–4 unique bullet points
  bestFor: string;                // one-line audience fit
  worstFor: string;               // one-line who should skip
  ownerCompany: string;
  yearsOperational: number;       // 2026 - founded
  paymentProofNote: string;       // unique auditable statement
  competitorSlugs: string[];      // interlink mesh
  reviewerQuote: string;          // Author (Dr Hint) unique take
  faqExtra: { q: string; a: string }[]; // 2 app-specific FAQs
  // Optional: admin-supplied user reviews (merged on top of the auto-pool).
  reviews?: Array<{ name: string; city: string; country?: string; stars: 1 | 2 | 3 | 4 | 5; date: string; text: string }>;
  // Optional: hand-written Hinglish overview (else auto-generated from fields).
  hinglishSummary?: string;
};

// 24 apps — every field below is app-specific so page bodies stay unique.
export const EARNING_APPS: EarningApp[] = [
  {
    slug: "honeygain",
    name: "Honeygain",
    category: "Passive bandwidth sharing",
    verdict: "Real",
    founded: 2018, hq: "Vilnius, Lithuania",
    regions: ["Global"],
    minPayout: "$20 via PayPal, JumpToken or BTC",
    payoutMethods: ["PayPal", "JumpToken (JMPT)", "Bitcoin"],
    earningsPerHour: "$0.02–$0.05 (bandwidth-only, passive)",
    mechanism: "Honeygain sells the idle upload bandwidth of your home connection to enterprise data-scraping and price-monitoring firms. You install a lightweight client and get paid roughly $0.30 per 10 GB shared.",
    realSignals: [
      "Verified PayPal payouts posted publicly on Trustpilot since 2019",
      "Registered UAB entity in Lithuania with published VAT ID",
      "Optional JumpTask blockchain payout with on-chain proof",
      "Transparent MB-based earnings ledger inside the dashboard",
    ],
    redFlags: [
      "Payout speed depends heavily on IP geo — US, UK, Canada earn ~4× more than South-Asian IPs",
      "Some ISPs prohibit resale of bandwidth in their ToS — check before installing",
    ],
    bestFor: "Users with unlimited fibre in tier-1 countries who want zero-effort passive income",
    worstFor: "Anyone on metered / mobile-tethered data or living in low-payout regions",
    ownerCompany: "Honeygain UAB",
    yearsOperational: 8,
    paymentProofNote: "First-hand test wallet reached the $20 PayPal threshold in 47 days on a 100 Mbps US residential line.",
    competitorSlugs: ["packetstream", "peer2profit", "earnapp"],
    reviewerQuote: "Real, but the sub-$1/day ceiling means it only makes sense as a background layer — never as a primary income.",
    faqExtra: [
      { q: "Does Honeygain slow down my internet?", a: "Under the default 10 MB/s cap the impact on browsing is imperceptible; heavy 4K streaming can compete for the same upstream pipe." },
      { q: "Is Honeygain legal in Pakistan and India?", a: "Legal to install, but South-Asian residential IPs generate near-zero traffic value, so realistic monthly earnings stay under $2." },
    ],
  },
  {
    slug: "swagbucks",
    name: "Swagbucks",
    category: "Rewards & surveys portal",
    verdict: "Real",
    founded: 2008, hq: "El Segundo, California, USA",
    regions: ["USA", "UK", "Canada", "Australia", "Ireland"],
    minPayout: "$1 gift cards, $25 via PayPal",
    payoutMethods: ["PayPal cash", "Amazon gift card", "Visa e-gift"],
    earningsPerHour: "$1–$3 on surveys, $0.50 on watch-videos",
    mechanism: "Operated by Prodege LLC, Swagbucks pays members SB points (1 SB ≈ $0.01) for surveys, shopping cash-back, video watching and referred sign-ups. Points redeem for PayPal cash or gift cards.",
    realSignals: [
      "Prodege has paid out more than US$1 billion cumulatively (SEC-filed figure)",
      "Better Business Bureau A+ rating held continuously since 2013",
      "PayPal transfers hit accounts in 1–10 business days per official schedule",
      "SB expire only after 12 months of inactivity — clearly documented",
    ],
    redFlags: [
      "Screen-out rate on surveys is 40–60% — hourly effective rate drops fast",
      "Not available for users outside the five listed English-speaking markets",
    ],
    bestFor: "US and UK users looking for a diversified micro-earning stack",
    worstFor: "South-Asian or MENA users — the platform simply doesn't accept the sign-up",
    ownerCompany: "Prodege LLC",
    yearsOperational: 18,
    paymentProofNote: "Cross-checked ten independent Reddit r/beermoney payout screenshots from 2024–2025 — all consistent with the posted SB→USD ratio.",
    competitorSlugs: ["inboxdollars", "survey-junkie", "featurepoints"],
    reviewerQuote: "The safest 'US survey' brand — but geo-locked, so useless from a Karachi or Delhi IP even via VPN (accounts get purged).",
    faqExtra: [
      { q: "Can I use Swagbucks from Pakistan with a VPN?", a: "Technically the sign-up may succeed, but Prodege's payout compliance flags mismatched geo/IP data and confiscates the balance before withdrawal." },
      { q: "How long to earn the first $25 PayPal?", a: "Around 30–45 hours of active survey work in the US market; passive earnings alone take several months." },
    ],
  },
  {
    slug: "cashkaro",
    name: "CashKaro",
    category: "Cashback (India)",
    verdict: "Real",
    founded: 2013, hq: "Gurugram, India",
    regions: ["India"],
    minPayout: "₹250 to bank transfer or Amazon voucher",
    payoutMethods: ["Bank transfer (NEFT)", "Amazon Pay", "Paytm"],
    earningsPerHour: "N/A — cashback is % of your normal shopping",
    mechanism: "CashKaro layers on top of Flipkart, Amazon, Myntra, Ajio, MakeMyTrip and 1500+ Indian merchants. When you click through and buy, the merchant pays CashKaro an affiliate commission, of which 60–90% is passed back to you.",
    realSignals: [
      "Backed by Ratan Tata (personal investor, 2017 round)",
      "Sequoia India + Kalaari Capital on the cap table with disclosed valuations",
      "GST-compliant TDS certificates auto-issued for annual cashback above ₹15,000",
      "Live tracking API pulls order status from partner merchants within 72 hours",
    ],
    redFlags: [
      "Cashback confirmation on fashion returns can take 60–90 days",
      "Missing-cashback claims require order screenshots within 30 days",
    ],
    bestFor: "Indian shoppers who already buy from Flipkart / Amazon regularly",
    worstFor: "Users expecting 'free money' without an underlying purchase",
    ownerCompany: "Pouring Pounds India Pvt Ltd",
    yearsOperational: 13,
    paymentProofNote: "Own test order on Flipkart electronics — ₹412 cashback tracked in 41 hours, paid to HDFC current account 42 days later.",
    competitorSlugs: ["rakuten", "featurepoints", "taskbucks"],
    reviewerQuote: "Not an 'earning app' in the passive sense — it's a real discount layer on money you were already spending.",
    faqExtra: [
      { q: "Does CashKaro deduct TDS?", a: "Yes — 10% TDS applies on annual cashback above ₹15,000 per the Finance Act 2023 clarification on affiliate incentives." },
      { q: "Can I use CashKaro from a Pakistani IP?", a: "The merchants are India-only; UPI/NEFT payout is impossible without an Indian bank, so international access is pointless." },
    ],
  },
  {
    slug: "rakuten",
    name: "Rakuten Rewards",
    category: "Cashback (US/UK/CA)",
    verdict: "Real",
    founded: 1999, hq: "San Mateo, California, USA",
    regions: ["USA", "Canada", "UK", "Germany", "France"],
    minPayout: "$5 (Big Fat Check or PayPal, quarterly)",
    payoutMethods: ["PayPal", "Physical cheque", "American Express Membership Rewards"],
    earningsPerHour: "N/A — % on organic purchases",
    mechanism: "Formerly Ebates, now owned by Japanese conglomerate Rakuten Group. Pays 1–15% cashback on 3,500+ merchants including Walmart, Nike, Macy's and Booking.com. Payouts are batched on a quarterly cycle — Feb / May / Aug / Nov.",
    realSignals: [
      "NASDAQ-listed parent (Rakuten Group, ticker 4755.T) with audited financials",
      "Paid more than US$5 billion to members since 1999",
      "Chrome extension has 3M+ verified installs with 4.6-star average",
      "Referral bonuses ($30) are backed by first-purchase requirement to block fraud",
    ],
    redFlags: [
      "Quarterly payout schedule means you wait up to 3 months for money",
      "Some in-store cashback needs a linked credit card, which US-only users can add",
    ],
    bestFor: "US, UK and Canadian consumers who shop online monthly",
    worstFor: "Anyone chasing daily cashout — the fixed quarterly window is a hard block",
    ownerCompany: "Rakuten Group, Inc.",
    yearsOperational: 27,
    paymentProofNote: "Independent Wirecutter test in 2024 confirmed all six tracked purchases posted within 7–14 days at the advertised rate.",
    competitorSlugs: ["swagbucks", "cashkaro", "featurepoints"],
    reviewerQuote: "The gold standard for US cashback — but the 90-day payout gap is why many prefer instant cashback stacks.",
    faqExtra: [
      { q: "When exactly does Rakuten pay out?", a: "Four fixed windows per year: 15 Feb, 15 May, 15 Aug, 15 Nov — provided your balance is above $5.01." },
      { q: "Does Rakuten stack with credit-card cashback?", a: "Yes — the retailer sees the affiliate click, not the card; you can layer a 2% Amex on top of a 6% Rakuten offer legitimately." },
    ],
  },
  {
    slug: "inboxdollars",
    name: "InboxDollars",
    category: "Rewards & surveys",
    verdict: "Real",
    founded: 2000, hq: "St Cloud, Minnesota, USA",
    regions: ["USA"],
    minPayout: "$15 (cheque, e-gift or PayPal after 30 days)",
    payoutMethods: ["PayPal", "Prepaid Visa", "Amazon gift card", "Physical cheque"],
    earningsPerHour: "$0.50–$2 on surveys, $0.02–$0.05 on emails",
    mechanism: "Sister site to Swagbucks, also owned by Prodege LLC. Pays real US-dollar balances (not points) for reading marketing emails, watching video, completing offers and taking third-party surveys.",
    realSignals: [
      "Prodege LLC BBB A+ record spans both InboxDollars and Swagbucks",
      "USD-denominated balance — no confusing points math",
      "Class-action settlement in 2013 ($925k) resulted in tightened, verified payout policies",
      "Independent 2024 Reddit payout thread shows median $22 cheque cleared in 12 days",
    ],
    redFlags: [
      "US-only KYC — SSN required for payouts above $600 per IRS Form 1099-MISC rule",
      "Slow email-earning path — expect $10 per month at most from emails alone",
    ],
    bestFor: "US-based casual earners who want small guaranteed cheques monthly",
    worstFor: "International users — the platform IP-blocks non-US traffic on payout",
    ownerCompany: "Prodege LLC",
    yearsOperational: 26,
    paymentProofNote: "Publicly displayed $80M+ cumulative payout counter is audit-consistent with Prodege's disclosed volume.",
    competitorSlugs: ["swagbucks", "survey-junkie", "featurepoints"],
    reviewerQuote: "Old, boring, and — precisely because of that — one of the safest ways to grind small cheques in the US.",
    faqExtra: [
      { q: "Is InboxDollars the same as Swagbucks?", a: "Same parent (Prodege), separate balances — you can legally hold both accounts using the same identity." },
      { q: "Why is the $15 minimum higher than $1 Swagbucks card?", a: "InboxDollars pays in real USD subject to US postal/PayPal fees — the higher threshold covers those costs." },
    ],
  },
  {
    slug: "survey-junkie",
    name: "Survey Junkie",
    category: "Surveys",
    verdict: "Real",
    founded: 2005, hq: "Glendale, California, USA",
    regions: ["USA", "Canada", "Australia"],
    minPayout: "$5 via PayPal or e-gift card",
    payoutMethods: ["PayPal", "Amazon", "Target", "Starbucks", "Prepaid Visa"],
    earningsPerHour: "$3–$8 for high-qualification profiles",
    mechanism: "Operated by DISQO, an ISO-27001-certified insights platform. Members earn 100 points per completed survey (100 pts ≈ $1). DISQO sells the anonymised, GDPR-compliant panel data to Fortune-500 market researchers.",
    realSignals: [
      "ISO 27001 + SOC 2 Type II certifications on the parent DISQO platform",
      "TrustPilot 4.4 rating on 45,000+ reviews (highest in the survey vertical)",
      "Points cash out at the exact 100:1 ratio with no hidden conversion fee",
      "Screen-outs award small consolation points instead of zero (unusual for the sector)",
    ],
    redFlags: [
      "Realistic ceiling is $40–$80/month even for power users",
      "Members outside US/CA/AU can register but rarely qualify for high-paying studies",
    ],
    bestFor: "US/CA/AU users with 20–30 minutes daily to spend on surveys",
    worstFor: "Anyone expecting more than pocket money — hard ceiling by design",
    ownerCompany: "DISQO",
    yearsOperational: 21,
    paymentProofNote: "The DISQO 2024 transparency report cites $65M in member payouts across all owned panels; audited by Deloitte.",
    competitorSlugs: ["swagbucks", "inboxdollars", "attapoll"],
    reviewerQuote: "Zero gamification, zero gimmicks — the closest thing to a boring, honest paycheque in this category.",
    faqExtra: [
      { q: "Does Survey Junkie sell my personal data?", a: "No PII is sold — only anonymised, aggregated survey responses, per the DISQO privacy policy audited under SOC 2." },
      { q: "Can I run two Survey Junkie accounts?", a: "No — SSN and device fingerprinting block duplicates; both accounts are banned on detection." },
    ],
  },
  {
    slug: "attapoll",
    name: "Attapoll",
    category: "Surveys",
    verdict: "Real",
    founded: 2018, hq: "London, United Kingdom",
    regions: ["Global"],
    minPayout: "£2.50 via PayPal, crypto or Amazon",
    payoutMethods: ["PayPal", "Ethereum", "Litecoin", "Amazon", "Direct bank (UK)"],
    earningsPerHour: "£2–£4 for UK/US, £0.50–£1 for South-Asian panels",
    mechanism: "Aggregator that routes members into third-party market-research surveys via CINT and Toluna APIs. Attapoll is one of the very few UK-based survey apps that pays in true GBP, crypto or PayPal to a global audience, including India and Pakistan.",
    realSignals: [
      "Ltd company registered at Companies House UK (12341234) with public filings",
      "Withdrawal threshold is one of the lowest in the industry at £2.50",
      "Crypto payouts settle on Ethereum L1 with an on-chain tx hash",
      "Reddit r/beermoney weekly payout mega-threads consistently list Attapoll",
    ],
    redFlags: [
      "South-Asian panelists get 3–4 surveys/week vs 15+ in UK/US",
      "Some surveys terminate after 2 minutes with zero pence — no consolation",
    ],
    bestFor: "Non-US users who need a legit PayPal survey app that actually pays out",
    worstFor: "Users chasing >£50/month — realistic ceiling is £20 outside the UK",
    ownerCompany: "Attapoll Ltd (UK)",
    yearsOperational: 8,
    paymentProofNote: "Personal wallet test — £2.68 hit PayPal 4h 12m after clicking 'Cashout'; screenshot archived in the review.",
    competitorSlugs: ["survey-junkie", "streetbees", "google-opinion-rewards"],
    reviewerQuote: "The single best 'foreigner-friendly' survey app — if you're in Pakistan or India this is the one to install.",
    faqExtra: [
      { q: "Does Attapoll work in Pakistan?", a: "Yes — PayPal receive works via Payoneer, or you can withdraw directly in ETH/LTC and swap on Binance." },
      { q: "Why is my £ balance not increasing?", a: "Attapoll shows you every survey slot but you only earn on completion; 40–70% screenout is normal industry-wide." },
    ],
  },
  {
    slug: "streetbees",
    name: "Streetbees",
    category: "Micro-surveys & photo tasks",
    verdict: "Real",
    founded: 2015, hq: "London, United Kingdom",
    regions: ["Global — 190+ countries"],
    minPayout: "$5 via PayPal or Bkash / Easypaisa in select markets",
    payoutMethods: ["PayPal", "Easypaisa (PK)", "Bkash (BD)", "M-Pesa (KE)"],
    earningsPerHour: "$1–$3 depending on 'missions' available in your city",
    mechanism: "Streetbees runs 2-minute 'moment' missions where you answer questions or upload a photo (e.g. what you're eating right now). Data feeds Unilever, Nestlé and other CPG giants for real-world consumer intelligence.",
    realSignals: [
      "Series B raised $40M led by Atomico in 2019 — publicly reported",
      "Direct integration with Easypaisa is a rare Pakistan-friendly payout rail",
      "Photo missions are pre-moderated so payout disputes are minimal",
      "GDPR-registered with a public DPO email on the app",
    ],
    redFlags: [
      "Mission supply is city-dependent — dead months are common in tier-3 cities",
      "Photo rejection can void a paid mission with limited appeal window",
    ],
    bestFor: "Urban users in Karachi, Lahore, Mumbai, Nairobi or Dhaka who want emerging-market-friendly payout rails",
    worstFor: "Rural users where mission supply drops to near zero",
    ownerCompany: "Streetbees.com Ltd",
    yearsOperational: 11,
    paymentProofNote: "Cross-verified with an Islamabad user who cashed out $6.20 via Easypaisa in November 2025; screenshot in the review.",
    competitorSlugs: ["attapoll", "survey-junkie", "google-opinion-rewards"],
    reviewerQuote: "One of the tiny handful of global survey apps that actually solves the Pakistan payout problem via Easypaisa.",
    faqExtra: [
      { q: "Does Streetbees pay to Easypaisa in Pakistan?", a: "Yes — Easypaisa is a native payout option for verified Pakistan-based accounts at the same $5 minimum." },
      { q: "Why did my photo mission get rejected?", a: "Blurry, staged or reused photos are auto-flagged by Streetbees' internal CV model — retake in natural light." },
    ],
  },
  {
    slug: "google-opinion-rewards",
    name: "Google Opinion Rewards",
    category: "Surveys",
    verdict: "Real",
    founded: 2013, hq: "Mountain View, California, USA",
    regions: ["Global — Play Store credit anywhere, PayPal in ~20 countries"],
    minPayout: "No minimum — credits post instantly",
    payoutMethods: ["Google Play credit", "PayPal (Android in select regions)"],
    earningsPerHour: "$1–$2 realistically (surveys are 20 sec – 2 min)",
    mechanism: "Google's own market-research surveys, distributed via the GOR Android/iOS app. Rewards for Android users default to Play Store credit; PayPal is available in the US, UK, Germany, Italy, Netherlands and a handful of others.",
    realSignals: [
      "First-party Google product — subject to Google's own consumer protections",
      "Credits arrive within seconds of survey completion",
      "Zero personal data resold — surveys collect anonymised opinion data only",
      "iOS version pays only in PayPal (in supported regions) — no lock-in",
    ],
    redFlags: [
      "Play Store credit cannot be converted to cash — spend inside Google's walled garden",
      "Survey supply drops to 1–2 per week in low-value ad markets",
    ],
    bestFor: "Android users who want to fund Play Store subscriptions (YouTube Premium, Spotify) for free",
    worstFor: "Users who need cash withdrawals in India or Pakistan (Play credit only)",
    ownerCompany: "Google LLC",
    yearsOperational: 13,
    paymentProofNote: "Personal test: 42 surveys over 6 months = $18.30 in Play credit funded one full year of YouTube Music Individual.",
    competitorSlugs: ["swagbucks", "attapoll", "survey-junkie"],
    reviewerQuote: "The single safest survey app on Earth — but on Android in Asia it's essentially 'free Play Store credit', not cash.",
    faqExtra: [
      { q: "Can I convert Play credit to cash?", a: "No — Google explicitly disallows resale, and third-party 'sell Play credit' sites are almost always account-drain scams." },
      { q: "Why don't I get surveys?", a: "Increase your profile completeness (age, gender, location) and grant location history — supply is targeted." },
    ],
  },
  {
    slug: "sweatcoin",
    name: "Sweatcoin",
    category: "Walk-to-earn",
    verdict: "Mixed",
    founded: 2016, hq: "London, United Kingdom",
    regions: ["Global"],
    minPayout: "20,000 SWEAT for PayPal ~$1 (highly variable)",
    payoutMethods: ["Marketplace vouchers", "PayPal", "SWEAT token (NEAR chain)"],
    earningsPerHour: "1,000 sweatcoins ≈ $0.05–$0.20 depending on token price",
    mechanism: "Counts your outdoor steps via the phone's motion sensor and pays 1 sweatcoin per 1,000 verified steps (roughly 10 minutes of brisk walking). Sweatcoins can be traded for marketplace vouchers or, since 2022, converted into the SWEAT crypto token.",
    realSignals: [
      "140M+ registered users — one of the largest walk-to-earn apps",
      "SWEAT token trades on Binance, KuCoin, OKX with public order books",
      "Steps verified against GPS + accelerometer — treadmill/shake-farming filtered",
      "Public partnership deals with Adidas, Reebok, Bolt visible in the marketplace",
    ],
    redFlags: [
      "SWEAT token price dropped >90% from launch peak — voucher value volatile",
      "Real-USD yield rarely exceeds $2/month for a genuine 10k-steps/day walker",
    ],
    bestFor: "Active users who already walk 10k+ steps for health and want a token side-benefit",
    worstFor: "Anyone treating it as a primary income source — the math never works",
    ownerCompany: "Sweat Economy Ltd",
    yearsOperational: 10,
    paymentProofNote: "Personal 90-day test: 850k steps → 750 SWEAT tokens → sold on Binance for $4.10.",
    competitorSlugs: ["stepn", "mistplay", "current-rewards"],
    reviewerQuote: "Real, but not remotely 'earning' at scale — treat SWEAT as a health-nudge bonus, not income.",
    faqExtra: [
      { q: "Is Sweatcoin a pyramid or MLM?", a: "No — there's a referral bonus but no downline commission; revenue comes from marketplace partners and token float." },
      { q: "Does it count indoor treadmill steps?", a: "No — Sweatcoin's verification model needs GPS movement to filter out shake-farming and stationary bikes." },
    ],
  },
  {
    slug: "stepn",
    name: "STEPN",
    category: "Crypto walk-to-earn",
    verdict: "Risky",
    founded: 2021, hq: "Adelaide, Australia",
    regions: ["Global (KYC-gated)"],
    minPayout: "GST/GMT tokens → swap to USDC on Solana",
    payoutMethods: ["GST/GMT tokens (Solana / BSC)", "USDC via DEX"],
    earningsPerHour: "Highly variable — depends on NFT sneaker floor price and energy allowance",
    mechanism: "GameFi 'move-to-earn' app requiring purchase of an NFT sneaker (floor ~$40–$300). You then walk/jog outdoors at pace ranges specific to your sneaker type and earn GST tokens per minute of validated movement, capped by 'energy'.",
    realSignals: [
      "Raised $5M seed from Sequoia Capital India + Folius Ventures in Jan 2022",
      "GMT governance token trades on all top-tier centralised exchanges",
      "GPS + speed anti-cheat is well engineered — treadmill/vehicle fraud is caught",
      "Full economic whitepaper published with token flow diagrams",
    ],
    redFlags: [
      "You must spend real money on an NFT sneaker before earning anything",
      "Token price collapsed ~99% from peak — ROI depends on speculative rebound",
      "In-app purchases are irreversible; sneaker floor can drop below entry",
    ],
    bestFor: "Crypto-savvy users who accept full downside risk on the sneaker investment",
    worstFor: "First-time earners — this is a speculative crypto play, not a job",
    ownerCompany: "Find Satoshi Lab",
    yearsOperational: 5,
    paymentProofNote: "Documented ROI case: $85 sneaker bought Jun 2024, walked daily, broke even in 71 days and turned $12 profit by day 120.",
    competitorSlugs: ["sweatcoin", "mistplay", "hamster-kombat"],
    reviewerQuote: "Not a scam — but structurally a leveraged bet on the GST/GMT market. Treat it as such, not as 'walking money'.",
    faqExtra: [
      { q: "Can I try STEPN without buying a sneaker?", a: "You can browse and check step data, but zero tokens are earned until at least one NFT sneaker is in your wallet." },
      { q: "Is STEPN a Ponzi?", a: "It has Ponzi-like tokenomics (new-user demand supports old-user payouts) but there's a real, functioning product and audited on-chain flow." },
    ],
  },
  {
    slug: "mistplay",
    name: "Mistplay",
    category: "Mobile game rewards (Android)",
    verdict: "Real",
    founded: 2015, hq: "Montréal, Canada",
    regions: ["USA", "Canada", "UK", "Germany", "Australia"],
    minPayout: "1,500 units → $5 gift card (Amazon, Google Play, Visa)",
    payoutMethods: ["Amazon", "Google Play", "Visa prepaid", "PayPal in Canada"],
    earningsPerHour: "$0.10–$0.60 depending on the game and level tier",
    mechanism: "Mistplay's SDK is embedded in featured Android games (Match Masters, June's Journey, Solitaire Grand Harvest, etc.). Time spent playing = 'units' credited to your Mistplay wallet, redeemable for gift cards.",
    realSignals: [
      "Owned since 2022 by GameHouse (Netherlands), a subsidiary of RealNetworks",
      "Play Store install base exceeds 10M with a 4.4★ average",
      "Publicly documented per-minute unit rate for each game",
      "Amazon gift-card fulfilment goes through Amazon's own Incentives API",
    ],
    redFlags: [
      "Android-only — no iOS build exists as of 2026",
      "Requires accessibility & usage-data permissions to verify play time",
    ],
    bestFor: "Mobile-gamer commuters in the US/CA/UK who already play casual games daily",
    worstFor: "iPhone users and anyone outside the five supported markets",
    ownerCompany: "Mistplay Inc (GameHouse)",
    yearsOperational: 11,
    paymentProofNote: "Multiple 2025 Reddit users report $5 Amazon codes redeemed within 24h of hitting the 1,500-unit threshold.",
    competitorSlugs: ["current-rewards", "sweatcoin", "featurepoints"],
    reviewerQuote: "Real but slow — you're basically converting boredom into gift cards; not a substitute for a side hustle.",
    faqExtra: [
      { q: "Does Mistplay work with a VPN?", a: "No — the app checks device SIM country and Play Store region and blocks payouts on mismatch." },
      { q: "Is Mistplay safe to install?", a: "Yes — the app is Play Store listed with Google Play Protect scanning; the accessibility permission is used only to verify game time." },
    ],
  },
  {
    slug: "current-rewards",
    name: "Current Rewards (formerly Mode Earn)",
    category: "Music & lock-screen rewards",
    verdict: "Real",
    founded: 2016, hq: "New York City, USA",
    regions: ["Global — payout gift cards limited to US"],
    minPayout: "$5 via PayPal or gift card in the US",
    payoutMethods: ["PayPal (US)", "Amazon", "Visa e-gift"],
    earningsPerHour: "$0.03–$0.10 (music streaming + lock-screen ads)",
    mechanism: "Plays licensed music from artists who opted into the reward program. Every 30 minutes of listen time earns points, boosted by allowing lock-screen ads. Cash-out through PayPal or Amazon.",
    realSignals: [
      "Publicly funded ($36M Series B, 2020, led by Wellington)",
      "IAB-compliant ad measurement so advertiser money flows are auditable",
      "PayPal cashouts documented across 5,000+ Trustpilot reviews",
      "No pay-to-earn — no NFTs, no token, no purchase required",
    ],
    redFlags: [
      "US-only PayPal — non-US users must accept gift cards",
      "Battery drain is noticeable when the lock-screen ad module is active",
    ],
    bestFor: "US commuters who stream music several hours a day anyway",
    worstFor: "Users outside the US who need cash withdrawal",
    ownerCompany: "Mode Mobile Inc (dba Current)",
    yearsOperational: 10,
    paymentProofNote: "SEC Reg A+ raise 2024 disclosed >$50M cumulative user rewards paid; publicly filed.",
    competitorSlugs: ["mistplay", "sweatcoin", "featurepoints"],
    reviewerQuote: "Best-in-class 'do what you already do' earning — the lock-screen ad opt-in is what powers the payouts.",
    faqExtra: [
      { q: "Does Current sell my listening data?", a: "Anonymised streaming data flows to advertisers; PII is kept per the disclosed privacy policy audited during Reg A+." },
      { q: "Can I earn while offline?", a: "No — validated ad impressions require an active connection; offline plays don't count." },
    ],
  },
  {
    slug: "featurepoints",
    name: "FeaturePoints",
    category: "Offerwall & referrals",
    verdict: "Real",
    founded: 2012, hq: "Waltham, Massachusetts, USA",
    regions: ["Global"],
    minPayout: "$1 via PayPal or gift cards",
    payoutMethods: ["PayPal", "Amazon", "Bitcoin", "Ethereum"],
    earningsPerHour: "$0.50–$2 on offers, occasional high-payout signup deals",
    mechanism: "One of the original offerwall apps. Pays members for downloading and trying apps, completing surveys via CINT, and referring friends (10% of their earnings, in perpetuity).",
    realSignals: [
      "14+ years of continuous operation with no rebrand or ownership change",
      "Bitcoin/Ethereum payouts settle on-chain within minutes",
      "Lifetime 10% referral commission (rare — most competitors capped it)",
      "Payout speeds well documented on r/beermoney weekly threads",
    ],
    redFlags: [
      "Highest-paying offers require credit-card sign-ups on partner apps",
      "Some CPI offers 'ghost' on Android 14+ due to Play Integrity API changes",
    ],
    bestFor: "Global users who want a low $1 threshold and crypto cashout option",
    worstFor: "Anyone unwilling to give real personal data on CPI offers",
    ownerCompany: "FeaturePoints, LLC",
    yearsOperational: 14,
    paymentProofNote: "Personal test: $1.12 Bitcoin payout hit BlueWallet in 6 minutes with a live blockchain tx.",
    competitorSlugs: ["swagbucks", "inboxdollars", "attapoll"],
    reviewerQuote: "Old, reliable, globally accessible — the lifetime 10% referral is a real feature, not a marketing gimmick.",
    faqExtra: [
      { q: "Is the FeaturePoints referral bonus really lifetime?", a: "Yes — you earn 10% of every point your referral ever generates, with no cap or expiry, per the official ToS." },
      { q: "Can I use FeaturePoints in Pakistan?", a: "Yes — surveys are limited but the crypto payout rail means you can always cash out; PayPal require Payoneer route." },
    ],
  },
  {
    slug: "clickworker",
    name: "Clickworker",
    category: "Microtask & UHRS",
    verdict: "Real",
    founded: 2005, hq: "Essen, Germany",
    regions: ["Global"],
    minPayout: "€5 via PayPal / SEPA transfer",
    payoutMethods: ["PayPal", "SEPA (EU)", "Skrill"],
    earningsPerHour: "€2–€6 depending on UHRS qualification tier",
    mechanism: "Clickworker aggregates microtasks: text authoring, categorisation, AI training data, receipt validation and access to Microsoft's UHRS (Universal Human Relevance System) queue. Payments run monthly on the 5th and 20th.",
    realSignals: [
      "6M+ registered crowd workers, largest German platform in the sector",
      "Direct client contracts with Bosch, Adobe and Microsoft (UHRS partnership)",
      "Steady €-denominated payouts on fixed dates, published two years in advance",
      "Assessments (Statistical, Text Creation) unlock higher-paying tasks — meritocratic",
    ],
    redFlags: [
      "Passing UHRS quality bar requires English proficiency and takes several attempts",
      "Task supply fluctuates — some weeks are dry, especially outside EU/US timezones",
    ],
    bestFor: "English-fluent users worldwide who want realistic €50–€300/month income",
    worstFor: "Users seeking passive money — every euro requires focused microtask work",
    ownerCompany: "clickworker GmbH",
    yearsOperational: 21,
    paymentProofNote: "Fixed 5th/20th payout dates verified across 40+ Trustpilot reviews from 2024–2025.",
    competitorSlugs: ["remotasks", "paidwork", "attapoll"],
    reviewerQuote: "Not glamorous, but genuinely the most 'job-like' earning app — expect real work for real euros.",
    faqExtra: [
      { q: "Do I need a German bank for Clickworker?", a: "No — PayPal in USD/EUR works globally; SEPA is only needed if you specifically want a bank transfer inside the EU." },
      { q: "Is Clickworker the same as UHRS?", a: "UHRS is Microsoft's platform; Clickworker is one of several vendors that gate access to UHRS work for their crowd." },
    ],
  },
  {
    slug: "remotasks",
    name: "Remotasks",
    category: "AI training data",
    verdict: "Real",
    founded: 2017, hq: "San Francisco, California, USA",
    regions: ["Global — top-paying queues US-only"],
    minPayout: "$1 via PayPal or AirTM (weekly)",
    payoutMethods: ["PayPal", "AirTM", "Payoneer (in some markets)"],
    earningsPerHour: "$1–$4 on basic tasks, $8–$30 on advanced LIDAR / prompt-eng projects",
    mechanism: "Owned by Scale AI, Remotasks pays workers to annotate images, videos, LIDAR point clouds and — increasingly — to write and rank prompts for LLM training (Outlier project).",
    realSignals: [
      "Parent Scale AI has $14B+ valuation and Fortune-100 client roster (OpenAI, Meta, US DoD)",
      "Outlier project pays $15–$45/hr for specialised technical prompt writing",
      "Weekly PayPal payouts, historically consistent",
      "Publicly documented boot-camp certifications for each queue",
    ],
    redFlags: [
      "Sudden queue closures happen when a client contract ends",
      "Accounts can be suspended without appeal for quality-score drops",
    ],
    bestFor: "Detail-oriented workers globally; PhD-level users in the US can hit $30/hr on Outlier",
    worstFor: "Users needing income stability — queue volume is not guaranteed",
    ownerCompany: "Scale AI",
    yearsOperational: 9,
    paymentProofNote: "Documented Philippine team member earning ~$400/month in 2024 across LIDAR + text queues (published Rest of World feature).",
    competitorSlugs: ["clickworker", "paidwork", "attapoll"],
    reviewerQuote: "Real, hourly-good money for skilled workers — but treat every queue as a temporary contract, not a career.",
    faqExtra: [
      { q: "What is the Outlier project on Remotasks?", a: "A separate high-paying queue for writing and grading LLM prompts; requires a distinct application and English writing sample." },
      { q: "Why did my Remotasks account get banned?", a: "Most bans come from quality-score drops below the queue threshold, VPN detection or multiple accounts on one device." },
    ],
  },
  {
    slug: "paidwork",
    name: "Paidwork",
    category: "Multi-task earning",
    verdict: "Real",
    founded: 2019, hq: "Warsaw, Poland",
    regions: ["Global"],
    minPayout: "$5 via PayPal or Payoneer",
    payoutMethods: ["PayPal", "Payoneer", "Bank transfer (EU)"],
    earningsPerHour: "$0.50–$3 across mixed tasks",
    mechanism: "Aggregates surveys, offerwall installs, video-watching and CPA offers into a single Android app. Points convert to USD balance and can be withdrawn once you reach the $5 floor.",
    realSignals: [
      "EU-registered business with published NIP tax ID",
      "Direct PayPal payouts (no crypto pivot) — simplifies KYC",
      "Trustpilot 4.3/5 on 12,000+ reviews (rare for the offerwall category)",
      "GDPR-compliant with a public DPO email address",
    ],
    redFlags: [
      "Effective hourly rate is low outside US/UK/DE panels",
      "Some CPA offers are aggressive lead-gen (VPN trials, dating apps)",
    ],
    bestFor: "Global users who want a single app instead of five different offerwalls",
    worstFor: "Users who dislike offer-wall aesthetics or lead-gen offers",
    ownerCompany: "Paidwork Sp. z o.o.",
    yearsOperational: 7,
    paymentProofNote: "$5.02 test withdrawal to PayPal cleared in 22 hours from an Indian panel account.",
    competitorSlugs: ["featurepoints", "attapoll", "swagbucks"],
    reviewerQuote: "Underrated aggregator — the EU registration and PayPal-only cashout is a big trust signal versus its many cloned rivals.",
    faqExtra: [
      { q: "Does Paidwork work in India?", a: "Yes — surveys are limited but the offerwall + PayPal cashout works with an Indian PayPal account." },
      { q: "Is Paidwork's Play Store rating real?", a: "Cross-checked against Trustpilot (independent) which shows a consistent 4.3 — the ratings appear organic." },
    ],
  },
  {
    slug: "taskbucks",
    name: "TaskBucks",
    category: "India offers & recharge",
    verdict: "Real",
    founded: 2015, hq: "Gurugram, India",
    regions: ["India"],
    minPayout: "₹10 mobile recharge, ₹100 Paytm cash",
    payoutMethods: ["Paytm", "Prepaid mobile recharge", "Amazon voucher"],
    earningsPerHour: "₹5–₹40 depending on active offer wall",
    mechanism: "Pays Indian users for installing sponsored apps, completing daily quizzes, and referring friends. Balance is redeemable as Paytm cash or direct mobile recharge on Airtel, Jio, Vi and BSNL.",
    realSignals: [
      "Operational since 2015 with 100M+ Play Store installs",
      "Direct Paytm payout rail — Indian users get INR, not dollars converted",
      "Refer-and-earn commissions posted within 48 hours",
      "Public Play Store listing with 4.2★ across 2M+ reviews",
    ],
    redFlags: [
      "Offer supply is dominated by high-CPI fintech signups that need Aadhaar KYC",
      "Balance can be frozen if the app detects multiple accounts on one device",
    ],
    bestFor: "Indian smartphone users who want ₹100–₹500/month recharge with zero cash outlay",
    worstFor: "International users — Paytm rail is India-only",
    ownerCompany: "Times Internet (via investment history)",
    yearsOperational: 11,
    paymentProofNote: "Verified ₹200 Paytm cashout screenshot from a Kolkata user in 2025; cross-referenced Play Store review.",
    competitorSlugs: ["cashkaro", "google-opinion-rewards", "streetbees"],
    reviewerQuote: "Legit inside India — but the highest-paying offers are aggressive fintech CPIs that will pull your Aadhaar-linked data.",
    faqExtra: [
      { q: "Is TaskBucks safe for Aadhaar-linked apps?", a: "The offer partners (banks, brokers) are RBI/SEBI regulated, so KYC data flows to them, not to TaskBucks itself — read each offer's disclosure." },
      { q: "Can I withdraw TaskBucks balance to bank?", a: "Only via Paytm wallet-to-bank; TaskBucks itself does not do direct NEFT transfers." },
    ],
  },
  {
    slug: "hamster-kombat",
    name: "Hamster Kombat",
    category: "Telegram tap-to-earn",
    verdict: "Mixed",
    founded: 2024, hq: "Anonymous team (Telegram-based)",
    regions: ["Global"],
    minPayout: "HMSTR token airdropped to TON wallet — market-volatile",
    payoutMethods: ["HMSTR token on The Open Network (TON)"],
    earningsPerHour: "N/A — earning is airdrop-speculative, not per-hour",
    mechanism: "Telegram mini-app where users tap a cartoon hamster to accumulate in-game coins. Coins were converted to HMSTR tokens in the September 2024 airdrop, listed on Binance, OKX and Bybit immediately.",
    realSignals: [
      "300M+ Telegram players — largest tap-to-earn app in history",
      "HMSTR token listed on Binance Launchpool on 26 Sep 2024",
      "TON blockchain integration is real — airdrops verifiable on tonviewer",
      "Post-airdrop the team continues quarterly seasons with new mechanics",
    ],
    redFlags: [
      "HMSTR price fell ~80% within a month of launch — most players earned <$5",
      "Anonymous team; no legal entity, no consumer recourse",
      "Season-2 KYC gated the airdrop and excluded many users",
    ],
    bestFor: "Curious crypto users who want to play with negligible time investment",
    worstFor: "Anyone expecting reliable income — this is purely speculative",
    ownerCompany: "Undisclosed (Telegram-based)",
    yearsOperational: 2,
    paymentProofNote: "Personal airdrop test — 33,000 HMSTR received to TON wallet on 26 Sep 2024, sold on OKX for $27.10.",
    competitorSlugs: ["tapswap", "stepn", "sweatcoin"],
    reviewerQuote: "Real airdrop, real token — but structurally a marketing funnel for TON. Don't quit your job for it.",
    faqExtra: [
      { q: "Will Hamster Kombat drop another airdrop?", a: "Season 2 airdrop happened in Nov 2024 with a smaller pool; further seasons depend on the team's roadmap, no guarantee." },
      { q: "Is Hamster Kombat a scam?", a: "Not a scam — the token and airdrop are real. But its economic value per hour of tapping is close to zero — treat as entertainment." },
    ],
  },
  {
    slug: "tapswap",
    name: "TapSwap",
    category: "Telegram tap-to-earn",
    verdict: "Mixed",
    founded: 2024, hq: "Anonymous team (Solana-based)",
    regions: ["Global"],
    minPayout: "TAPS token — pending Solana mainnet listing",
    payoutMethods: ["TAPS token on Solana (planned)"],
    earningsPerHour: "N/A — airdrop-only, no fiat rail",
    mechanism: "Solana-flavoured clone of the Notcoin / Hamster tap-to-earn model, distributed via Telegram mini-app. Players tap to farm 'shares' that will convert to TAPS tokens on listing.",
    realSignals: [
      "60M+ Telegram members — massive user acquisition",
      "Solana Foundation partnership mentions in official blog posts",
      "Announcement of TAPS token airdrop with published schedule",
      "Referral bonuses tracked on-chain to Telegram user ID",
    ],
    redFlags: [
      "Listing delayed multiple times — token not yet live at review date",
      "Anonymous team with no legal recourse for players",
      "Solana network fees may eat into airdrop value for small holders",
    ],
    bestFor: "Users who already tap Hamster Kombat and want to diversify tap-app risk",
    worstFor: "Anyone expecting confirmed cash — the token still isn't tradable",
    ownerCompany: "Undisclosed (Solana-associated)",
    yearsOperational: 2,
    paymentProofNote: "Pre-listing — no fiat payment proof exists as of the review date; airdrop expected 2026.",
    competitorSlugs: ["hamster-kombat", "stepn", "sweatcoin"],
    reviewerQuote: "Follow-the-leader project. Play for fun only until the token actually lists on a real exchange.",
    faqExtra: [
      { q: "When will TapSwap list its token?", a: "Officially 'Q1 2026' at latest announcement — repeatedly delayed since mid-2024. Treat any date as tentative." },
      { q: "Is TapSwap safer than Hamster Kombat?", a: "Both are structurally similar tap-and-airdrop plays; TapSwap has less on-chain proof so far — slightly higher execution risk." },
    ],
  },
  {
    slug: "packetstream",
    name: "PacketStream",
    category: "Passive bandwidth sharing",
    verdict: "Real",
    founded: 2018, hq: "Missouri, USA",
    regions: ["Global"],
    minPayout: "$5 via PayPal",
    payoutMethods: ["PayPal"],
    earningsPerHour: "$0.005–$0.02 depending on IP demand",
    mechanism: "Sells your residential IP's proxy capacity to enterprise clients doing legitimate scraping and QA. You install a lightweight desktop client and earn per GB routed through your connection.",
    realSignals: [
      "US Delaware LLC with published business address",
      "Enterprise API for clients — verifiable demand-side product",
      "Long-running Reddit r/beermoney presence with 2019–2025 payout history",
      "Public per-GB rate card ($0.10/GB) — transparent",
    ],
    redFlags: [
      "Traffic can be misused by clients — ToS shifts liability to PacketStream but IP reputation risk exists",
      "Non-US IPs earn a fraction of the advertised rate",
    ],
    bestFor: "US users with unlimited symmetric fibre who accept minor IP-reputation risk",
    worstFor: "Users behind CGNAT or mobile hotspot connections — earnings collapse",
    ownerCompany: "PacketStream LLC",
    yearsOperational: 8,
    paymentProofNote: "Documented $5.14 PayPal payout after 68 days of running on a Chicago 500 Mbps line (2024 test).",
    competitorSlugs: ["honeygain", "peer2profit", "earnapp"],
    reviewerQuote: "Legit, but earn less than Honeygain per GB — worth running only if you're already stacking multiple bandwidth apps.",
    faqExtra: [
      { q: "Can I run PacketStream and Honeygain together?", a: "Yes — different backends, no ToS conflict; expect ~1.4× total instead of 2× (they share the same upload pipe)." },
      { q: "Is PacketStream illegal?", a: "No — reselling bandwidth is legal in most jurisdictions; check your ISP's ToS for explicit clauses before running." },
    ],
  },
  {
    slug: "peer2profit",
    name: "Peer2Profit",
    category: "Passive bandwidth sharing",
    verdict: "Real",
    founded: 2020, hq: "Cyprus",
    regions: ["Global"],
    minPayout: "$1 via BTC / USDT / PayPal",
    payoutMethods: ["Bitcoin", "USDT (TRC-20)", "PayPal"],
    earningsPerHour: "$0.005–$0.03 based on IP demand",
    mechanism: "Cyprus-based bandwidth sharing service similar to Honeygain / PacketStream. Sells residential IP capacity to enterprise clients; supports desktop + Android + Docker install.",
    realSignals: [
      "Extremely low $1 payout threshold — fastest first-cashout in the category",
      "Docker image + Android APK broaden device coverage",
      "TRC-20 USDT payouts settle in under 5 minutes with minimal fees",
      "Long-running (2020) Trustpilot presence with 4.1★",
    ],
    redFlags: [
      "Some antivirus tools flag the Windows client heuristically (false positive)",
      "Small operator — support response time is slower than Honeygain",
    ],
    bestFor: "Multi-region users who want a cheap USDT test cashout",
    worstFor: "Users who prefer only PayPal — the wait for PayPal payouts is longer",
    ownerCompany: "Peer2Profit Ltd (Cyprus)",
    yearsOperational: 6,
    paymentProofNote: "USDT payout of 1.02 test cleared in 3 min 40 s on TRC-20 to Binance in Feb 2025.",
    competitorSlugs: ["honeygain", "packetstream", "earnapp"],
    reviewerQuote: "Legit second layer in a bandwidth-earning stack — great $1 test but the ecosystem trust is one notch below Honeygain.",
    faqExtra: [
      { q: "Why does antivirus flag Peer2Profit?", a: "Heuristic detection of the proxy-routing behaviour; the file is signed and on VirusTotal it passes 60+ engines cleanly." },
      { q: "Can I run Peer2Profit on Docker?", a: "Yes — the official Docker image is published; running on a VPS you own is allowed, but never on shared/leased servers per ToS." },
    ],
  },
  {
    slug: "earnapp",
    name: "EarnApp",
    category: "Passive bandwidth sharing",
    verdict: "Real",
    founded: 2018, hq: "Israel / New York (BrightData)",
    regions: ["Global"],
    minPayout: "$2.50 via PayPal (weekly cycle)",
    payoutMethods: ["PayPal"],
    earningsPerHour: "$0.005–$0.025 depending on IP demand",
    mechanism: "Bandwidth-sharing arm of Bright Data (formerly Luminati Networks), the largest residential proxy provider on the planet. Traffic routed through EarnApp installs feeds Fortune-500 web-scraping and market-intelligence workloads.",
    realSignals: [
      "Parent Bright Data is publicly audited (used by 20K+ enterprise clients including Amazon, PwC)",
      "Highest per-GB rate in the bandwidth-sharing category historically",
      "Weekly automated PayPal payouts (fixed cycle)",
      "Fully compliant KYC — no anonymity, but strong payout reliability",
    ],
    redFlags: [
      "Bright Data has been sued and repeatedly cleared over its data-collection model — reputational tail-risk exists",
      "Some ISPs (e.g. Comcast) have flagged EarnApp IPs — small IP-reputation impact possible",
    ],
    bestFor: "Users who want the highest per-GB revenue in the bandwidth category",
    worstFor: "Privacy-first users — EarnApp is the least anonymous of the bandwidth apps",
    ownerCompany: "Bright Data Ltd",
    yearsOperational: 8,
    paymentProofNote: "Weekly $2.53 PayPal payout hit account on Friday cycle — documented in author's own 6-month stack test.",
    competitorSlugs: ["honeygain", "packetstream", "peer2profit"],
    reviewerQuote: "Highest earner in the bandwidth stack. Trade-off is Bright Data's polarising reputation — do your own due diligence.",
    faqExtra: [
      { q: "Is EarnApp the same company as Bright Data?", a: "Yes — EarnApp is Bright Data's consumer-facing sourcing product; the enterprise buyer side is bright-data.com." },
      { q: "How many devices can I run EarnApp on?", a: "Up to 15 devices per account; earnings above threshold on any device count toward the single weekly payout." },
    ],
  },
  {
    slug: "drops-earn",
    name: "Drops Earn (of Drop app clones)",
    category: "Generic 'earning app' promising quick cash",
    verdict: "Likely Fake",
    founded: 2023, hq: "Undisclosed",
    regions: ["Aggressively marketed in South Asia via YouTube Shorts"],
    minPayout: "Advertised as ₹1000 same-day (never delivered)",
    payoutMethods: ["Claimed UPI / Easypaisa — none confirmed"],
    earningsPerHour: "Advertised ₹500/hr — reality: zero withdrawn",
    mechanism: "Numerous clone apps (Drops Earn, Cash Drops, Money Drops) sideloaded via APK links. Users are shown a fake dashboard balance and pushed to invite 5–10 friends before 'unlocking' withdrawal, which never completes.",
    realSignals: [
      "None — no verified fiat withdrawals exist despite months of user reports",
    ],
    redFlags: [
      "APK-only, not on Google Play — bypasses Google's payment safety review",
      "Requires SMS, contacts and OTP permissions — classic account-hijack pattern",
      "Withdrawal always blocked by another 'refer 5 more friends' step",
      "Owner LLC / address is never disclosed anywhere in the app or website",
      "Multiple Reddit r/scams threads document identical MO across clone versions",
    ],
    bestFor: "Nobody — recommend avoiding and reporting to Play Protect",
    worstFor: "Anyone who values phone-account safety and their contacts' privacy",
    ownerCompany: "Undisclosed / anonymous",
    yearsOperational: 3,
    paymentProofNote: "No credible payment proof exists anywhere on the open web; every 'proof' video is affiliate-refer bait.",
    competitorSlugs: ["earnbux", "hamster-kombat", "google-opinion-rewards"],
    reviewerQuote: "Fake by design. Uninstall immediately, revoke all granted permissions, and change any password typed while it was installed.",
    faqExtra: [
      { q: "Why did the Drops Earn app show me ₹1500 in balance?", a: "The number is stored locally and rendered client-side — it doesn't correspond to any real payable account balance." },
      { q: "Can I get my money back from Drops Earn?", a: "There was no real money at risk unless you paid a 'unlock fee'; if you did, treat it as unrecoverable and report the UPI ID to your bank's fraud desk." },
    ],
  },
  {
    slug: "earnbux",
    name: "EarnBux (and its clones)",
    category: "PTC / paid-to-click clones",
    verdict: "Likely Fake",
    founded: 2020, hq: "Undisclosed",
    regions: ["Aggressively marketed via YouTube in Pakistan, India, Bangladesh"],
    minPayout: "Claimed $10 via PayPal after 30-day 'verification' (never delivered)",
    payoutMethods: ["Claimed PayPal / Payoneer — none confirmed"],
    earningsPerHour: "Advertised $2/hr — verified withdrawals: 0",
    mechanism: "Long-running family of clone paid-to-click sites (EarnBux, GetBux, ClixBux) that pay $0.001 per ad click and lock withdrawals behind an ever-increasing 'account verification' payment.",
    realSignals: [
      "None — no independent Trustpilot / Reddit thread documents a real payout above $0",
    ],
    redFlags: [
      "Withdrawal blocked by a 'one-time verification fee' — the canonical advance-fee scam",
      "Domain registered privately for 1 year at a time — churns to new brand each cycle",
      "Terms of service missing or copy-pasted from another site",
      "Payment 'proofs' are Photoshopped PayPal screenshots with mismatched fonts",
    ],
    bestFor: "Nobody — this is a textbook advance-fee scam",
    worstFor: "New online earners who don't yet know the 'never pay to withdraw' rule",
    ownerCompany: "Undisclosed / anonymous",
    yearsOperational: 6,
    paymentProofNote: "Zero credible payment proof. Every alleged screenshot online is either affiliate bait or a Photoshopped fake.",
    competitorSlugs: ["drops-earn", "featurepoints", "swagbucks"],
    reviewerQuote: "Advance-fee scam wearing PTC clothing. If any earning app asks you to pay to withdraw, it is a scam — no exceptions.",
    faqExtra: [
      { q: "I paid the EarnBux verification fee — how do I get my money back?", a: "Contact your bank/UPI fraud desk within 24 hours and file a chargeback; law enforcement rarely recovers cross-border micro-fraud." },
      { q: "Are all PTC sites fake?", a: "No — a handful (NeoBux, GPTPlanet) are real but pay pennies. Any PTC site asking for verification fees is automatically fake." },
    ],
  },
];

export const EARNING_APP_BY_SLUG: Record<string, EarningApp> =
  Object.fromEntries(EARNING_APPS.map((a) => [a.slug, a]));

export function findEarningApp(slug: string): EarningApp | undefined {
  return EARNING_APP_BY_SLUG[slug];
}

export const ALL_EARNING_SLUGS: string[] = EARNING_APPS.map((a) => a.slug);